if (!customElements.get('sissilux-seo-article')) {
  customElements.define('sissilux-seo-article', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.body = this.querySelector('[data-article-body]');
      this.inline = [...this.querySelectorAll('[data-inline-product]')].find(node => node.dataset.productUrl === this.dataset.primaryProductUrl);
      this.bar = this.querySelector('[data-mobile-bar]');
      this.seenProduct = false;
      this.tocDialog = this.querySelector('[data-toc-dialog]');
      this.tocTrigger = this.querySelector('[data-open-toc]');
      this.tocEntry = this.querySelector('[data-mobile-toc]');
      this.floatingToc = document.createElement('div');
      this.floatingToc.className = 'sx-floating-toc';
      this.floatingToc.hidden = true;
      this.floatingToc.append(this.tocTrigger.cloneNode(true));
      this.floatingToc.querySelector('strong').textContent = this.tocDialog.querySelector('h2').textContent;
      this.floatingToc.querySelector('button>span:last-child').textContent = '⌄';
      this.append(this.floatingToc);
      this.enhanceContent(signal);
      const headingLevel = ['h2', 'h3', 'h4'].find(level => this.body.querySelector(level));
      const headings = headingLevel ? [...this.body.querySelectorAll(headingLevel)] : [];
      headings.forEach((h, i) => { if (!h.id) h.id = `seo-${this.dataset.sectionId}-${i}`; });
      if (headings.length) {
        for (const nav of this.querySelectorAll('[data-toc],[data-mobile-toc-links]')) {
          nav.replaceChildren();
          headings.forEach((h, i) => {
            const a = document.createElement('a');
            a.href = `#${h.id}`;
            const number = document.createElement('span');
            number.className = 'sx-toc-number';
            number.textContent = String(i + 1).padStart(2, '0');
            number.setAttribute('aria-hidden', 'true');
            const label = document.createElement('span');
            label.textContent = h.dataset.tocLabel || h.textContent;
            a.append(number, label);
            a.addEventListener('click', e => {
              e.preventDefault();
              if (this.tocDialog.open) this.tocDialog.close();
              h.setAttribute('tabindex', '-1');
              h.focus({ preventScroll: true });
              h.scrollIntoView({ behavior: 'auto', block: 'start' });
              history.replaceState(history.state, '', '#' + encodeURIComponent(h.id));
              this.schedule();
            }, { signal });
            nav.append(a);
          });
        }
        this.querySelector('[data-toc-panel]').hidden = false;
        this.querySelector('[data-mobile-toc]').hidden = false;
      }
      this.querySelectorAll('[data-chapter-count]').forEach(n => n.textContent = String(headings.length));
      this.querySelectorAll('[data-open-toc]').forEach(trigger => trigger.addEventListener('click', () => {
        this.savedOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.tocDialog.showModal();
        this.querySelectorAll('[data-open-toc]').forEach(n => n.setAttribute('aria-expanded', 'true'));
        this.tocDialog.querySelector('a[aria-current="location"]')?.scrollIntoView({ block: 'nearest' });
        this.update();
      }, { signal }));
      this.tocDialog.addEventListener('close', () => {
        document.documentElement.style.overflow = this.savedOverflow ?? '';
        this.querySelectorAll('[data-open-toc]').forEach(n => n.setAttribute('aria-expanded', 'false'));
        this.update();
      }, { signal });
      this.querySelector('[data-close-toc]').addEventListener('click', () => this.tocDialog.close(), { signal });
      this.tocDialog.addEventListener('click', e => {
        const r = this.tocDialog.getBoundingClientRect();
        if (e.target === this.tocDialog && (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom)) this.tocDialog.close();
      }, { signal });
      if (this.inline) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(e => e.isIntersecting)) this.seenProduct = true;
          this.update();
        }, { threshold: .15 });
        this.observer.observe(this.inline);
      }
      this.addEventListener('change', e => {
        if (!e.target.matches('[data-seo-variant]')) return;
        const card = e.target.closest('[data-seo-product]');
        const input = card.querySelector('input[name="id"]');
        input.value = e.target.value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        const option = e.target.selectedOptions[0];
        card.querySelector('[data-variant-price]').textContent = option.dataset.price;
        const compare = option.dataset.comparePrice || '';
        card.querySelector('[data-compare-price]').textContent = compare;
        card.querySelector('[data-compare-wrapper]').hidden = !compare;
      }, { signal });
      this.addEventListener('submit', e => {
        const qty = e.target.querySelector('[data-seo-quantity]');
        if (qty && !qty.checkValidity()) { e.preventDefault(); e.stopImmediatePropagation(); qty.reportValidity(); }
      }, { capture: true, signal });
      this.querySelector('[data-open-product]')?.addEventListener('click', () => {
        this.inline.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        const control = this.inline.querySelector('select,button') || this.inline.querySelector('a');
        control?.focus({ preventScroll: true });
      }, { signal });
      this.update = () => {
        const mobile = matchMedia('(max-width: 749px)').matches;
        const header = mobile ? document.querySelector('sticky-header') || document.querySelector('sticky-group-manager') : document.querySelector('sticky-group-manager');
        const rect = header?.getBoundingClientRect();
        const offset = rect && rect.bottom > 0 && rect.top < 100 ? Math.min(rect.bottom + 20, 250) : 12;
        this.style.setProperty('--sx-top', `${offset}px`);
        const bodyRect = this.body.getBoundingClientRect();
        const progress = Math.max(0, Math.min(1, (offset - bodyRect.top) / Math.max(1, this.body.offsetHeight - innerHeight + offset)));
        this.querySelector('[data-read-progress]').style.width = `${progress * 100}%`;
        this.querySelector('[data-read-label]').textContent = `${Math.round(progress * 100)}%`;
        const mode = mobile ? this.dataset.mobileMode : this.dataset.desktopMode;
        const inlineRect = this.inline?.getBoundingClientRect();
        const overlayOpen = !!document.querySelector('cart-drawer.active,menu-drawer details[open],header-drawer[open],dialog[open],.menu-drawer-container[open]');
        const headerSection = header?.closest('.section-header');
        const headerVisible = !!rect && (rect.bottom > 1 || !!headerSection && !headerSection.classList.contains('shopify-section-header-hidden'));
        const floatTop = 0;
        const pastEntry = headings.length > 0 && this.tocEntry.getBoundingClientRect().bottom < floatTop;
        const floatHeight = this.floatingToc.offsetHeight || 48;
        this.style.setProperty('--sx-toc-top', `${floatTop}px`);
        this.floatingToc.hidden = !mobile || headerVisible || overlayOpen || !pastEntry || bodyRect.bottom <= floatTop + floatHeight + 12;
        const readingOffset = mobile && pastEntry ? Math.max(offset, floatTop + floatHeight + 12) : offset;
        this.style.setProperty('--sx-anchor-offset', `${readingOffset + 12}px`);
        if (this.bar) this.bar.hidden = !mobile || overlayOpen || bodyRect.bottom < 0 || !(mode === 'strong' && progress > .1 || mode === 'standard' && this.seenProduct && inlineRect?.bottom < offset);
        if (matchMedia('(min-width: 1320px)').matches && this.tocDialog.open) this.tocDialog.close();
        let current = headings[0]?.id;
        for (const h of headings) if (h.getBoundingClientRect().top < readingOffset + 50) current = h.id;
        this.querySelectorAll('[data-toc] a,[data-mobile-toc-links] a').forEach(a => {
          if (decodeURIComponent(a.hash.slice(1)) === current) a.setAttribute('aria-current', 'location');
          else a.removeAttribute('aria-current');
        });
        const activeHeading = headings.find(h => h.id === current);
        if (activeHeading) this.querySelectorAll('[data-current-chapter]').forEach(n => n.textContent = activeHeading.dataset.tocLabel || activeHeading.textContent);
      };
      let ticking = false;
      this.schedule = () => { if (!ticking) { ticking = true; requestAnimationFrame(() => { this.update(); ticking = false; }); } };
      window.addEventListener('scroll', this.schedule, { passive: true, signal });
      window.addEventListener('resize', this.schedule, { passive: true, signal });
      const revealHash = () => {
        let id;
        try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
        const target = document.getElementById(id);
        if (!target || !this.body.contains(target)) return;
        const details = target.closest('details');
        if (details) details.open = true;
      };
      window.addEventListener('hashchange', revealHash, { signal });
      revealHash();
      this.mutationObserver = new MutationObserver(this.schedule);
      const drawer = document.querySelector('cart-drawer');
      if (drawer) this.mutationObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      this.querySelectorAll('header-drawer,menu-drawer,.menu-drawer-container').forEach(n => this.mutationObserver.observe(n, { attributes: true, subtree: true, attributeFilter: ['open'] }));
      const nativeHeaderSection = document.querySelector('sticky-header')?.closest('.section-header');
      if (nativeHeaderSection) {
        this.mutationObserver.observe(nativeHeaderSection, { attributes: true, attributeFilter: ['class'] });
        nativeHeaderSection.addEventListener('transitionend', this.schedule, { signal });
      }
      this.update();
    }
    enhanceContent(signal) {
      const tables = [...this.body.querySelectorAll('.sx-table-scroll')];
      const measure = () => tables.forEach(region => {
        const wide = region.scrollWidth > region.clientWidth + 2;
        region.tabIndex = wide ? 0 : -1;
        region.classList.toggle('is-wide', wide);
        region.previousElementSibling?.classList.contains('sx-table-hint') && (region.previousElementSibling.hidden = !wide);
      });
      tables.forEach((region, i) => {
        const table = region.querySelector('table');
        const caption = table?.querySelector('caption');
        if (caption) {
          if (!caption.id) caption.id = 'table-caption-' + this.dataset.sectionId + '-' + i;
          region.setAttribute('aria-labelledby', caption.id);
          region.removeAttribute('aria-label');
        }
        if (!region.previousElementSibling?.classList.contains('sx-table-hint')) {
          const hint = document.createElement('p');
          hint.className = 'sx-table-hint'; hint.textContent = this.dataset.tableHint;
          hint.id = 'table-hint-' + this.dataset.sectionId + '-' + i;
          region.before(hint); region.setAttribute('aria-describedby', hint.id);
        }
      });
      this.tableObserver = new ResizeObserver(measure);
      tables.forEach(region => this.tableObserver.observe(region));
      measure();
      this.body.querySelectorAll('h3,h4').forEach(h => {
        if (/^step\s+\d+\s*[:.]/i.test(h.textContent.trim())) h.classList.add('sx-step-heading');
      });
      // Only an explicit FAQ heading starts enhancement; never infer medical meaning.
      const faqHeading = [...this.body.querySelectorAll('h2')].find(h => /^(frequently asked questions|faq)s?$/i.test(h.textContent.trim()));
      if (faqHeading) {
        let node = faqHeading.nextElementSibling;
        while (node && node.tagName !== 'H2') {
          if (node.tagName !== 'H3') { node = node.nextElementSibling; continue; }
          const question = node, answer = [];
          node = node.nextSibling;
          while (node && !(node.nodeType === 1 && /^(H2|H3)$/.test(node.tagName))) {
            answer.push(node); node = node.nextSibling;
          }
          if (!answer.some(n => n.textContent.trim())) continue;
          const details = document.createElement('details'); details.className = 'sx-faq';
          const summary = document.createElement('summary');
          const content = document.createElement('div'); content.className = 'sx-faq__answer';
          question.before(details); summary.append(question); content.append(...answer);
          details.append(summary, content);
        }
      }
      this.body.querySelectorAll('a[href^="#"]').forEach((citation, i) => {
        if (!citation.closest('sup') && !citation.matches('[role="doc-noteref"],[data-citation]')) return;
        let id; try { id = decodeURIComponent(citation.hash.slice(1)); } catch { return; }
        const source = document.getElementById(id);
        if (!source || !this.body.contains(source)) return;
        if (!citation.id) citation.id = 'citation-' + this.dataset.sectionId + '-' + i;
        if (source.querySelector('a.sx-reference-back')) return;
        const back = document.createElement('a'); back.className = 'sx-reference-back';
        back.href = '#' + citation.id; back.textContent = ' ↩';
        back.setAttribute('aria-label', this.dataset.referenceBack);
        source.append(back);
      });
      this.body.addEventListener('click', event => {
        const a = event.target.closest('a[href^="#"]');
        if (!a) return;
        let id; try { id = decodeURIComponent(a.hash.slice(1)); } catch { return; }
        const target = document.getElementById(id);
        if (a.matches('[role="doc-noteref"],[data-citation]') || a.closest('sup')) {
          const back = target?.querySelector('a.sx-reference-back');
          if (back && a.id) back.href = '#' + a.id;
        }
        if (target?.closest('details')) target.closest('details').open = true;
      }, { signal });
    }
    disconnectedCallback() {
      this.floatingToc?.remove();
      this.tableObserver?.disconnect();
      if (this.tocDialog?.open) { this.tocDialog.close(); document.documentElement.style.overflow = this.savedOverflow ?? ''; }
      this.controller?.abort(); this.observer?.disconnect(); this.mutationObserver?.disconnect(); this.controller = null;
    }
  });
}
